import { Check, Copy } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { PromptTextWithMentions } from "@/components/prompt-editor/prompt-text-with-mentions"
import { textContainsPromptTokens } from "@/components/prompt-editor/serialize"
import type { MentionItem } from "@/components/prompt-editor/types"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { useTranslation } from "@/hooks/use-translation"
import type { AgentSavedPromptCard } from "@/lib/agent-api"
import { cn } from "@/lib/utils"

const SAVED_PROMPT_KIND_KEYS = {
  prompt: "agent.savedPrompt.prompt",
  video: "agent.savedPrompt.video",
  shooting_board: "agent.savedPrompt.shootingBoard",
  image: "agent.savedPrompt.image",
} as const

function savedPromptKindKey(target: string) {
  switch (target) {
    case "prompt":
    case "video":
    case "shooting_board":
    case "image":
      return SAVED_PROMPT_KIND_KEYS[target]
    default:
      return SAVED_PROMPT_KIND_KEYS.image
  }
}

type Props = {
  prompts: AgentSavedPromptCard[]
  mentionItems?: MentionItem[]
  className?: string
}

export function AgentSavedPromptBlock({ prompts, mentionItems, className }: Props) {
  const { t } = useTranslation("workspace")
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const items = mentionItems ?? []
  const canResolveMentions = items.length > 0

  if (!prompts.length) return null

  const copyText = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      toast.success(t("agent.savedPrompt.copied"))
      window.setTimeout(() => {
        setCopiedKey((prev) => (prev === key ? null : prev))
      }, 1500)
    } catch {
      toast.error(t("agent.savedPrompt.copyFailed"))
    }
  }

  return (
    <div className={cn("mt-2 space-y-2", className)}>
      {prompts.map((prompt, index) => {
        const key = `${prompt.node_id ?? prompt.shot_id}:${prompt.target}:${index}`
        const kindLabel = t(savedPromptKindKey(prompt.target))
        const title = [kindLabel, prompt.label].filter(Boolean).join(" · ")
        const showMentions = canResolveMentions && textContainsPromptTokens(prompt.text)
        return (
          <div key={key} className="overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken/60">
            <div className="flex items-center justify-between gap-2 border-b border-ui-border/80 px-2.5 py-1.5">
              <span className="min-w-0 truncate text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">
                {title}
              </span>
              <ActionHintTooltip label={t("agent.savedPrompt.copy")}>
                <button
                  type="button"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-primary"
                  aria-label={t("agent.savedPrompt.copy")}
                  onClick={() => void copyText(key, prompt.text)}
                >
                  {copiedKey === key ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </ActionHintTooltip>
            </div>
            {showMentions ? (
              <PromptTextWithMentions text={prompt.text} items={items} />
            ) : (
              <pre className="it2tv-scrollbar max-h-48 overflow-auto px-2.5 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-ui-text-primary">
                {prompt.text}
              </pre>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function parseSavedPromptCards(meta: Record<string, unknown> | null | undefined): AgentSavedPromptCard[] {
  if (!meta || meta.kind !== "saved_prompts") return []
  const raw = meta.prompts
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") return []
    const row = item as Record<string, unknown>
    const text = typeof row.text === "string" ? row.text : ""
    if (!text.trim()) return []
    const shotId = Number(row.shot_id)
    const nodeId = Number(row.node_id)
    const hasShot = Number.isFinite(shotId) && shotId > 0
    const hasNode = Number.isFinite(nodeId) && nodeId > 0
    if (!hasShot && !hasNode) return []
    return [
      {
        ...(hasShot ? { shot_id: shotId } : {}),
        ...(hasNode ? { node_id: nodeId } : {}),
        label: typeof row.label === "string" ? row.label : null,
        target: typeof row.target === "string" ? row.target : "image",
        text,
      },
    ]
  })
}
